import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formService } from '../../services/formService';

/**
 * Route guard.
 *
 * Roles: superadmin (platform owner) > companyadmin (a company's own admin) >
 * staff. `requireAdmin` admits the two admin roles; `requireSuperadmin` admits
 * only the platform owner, for pages that span companies. `companyAdminOnly`
 * keeps the platform owner out of the tenant form workspace: the owner supports
 * companies, but does not author or process their forms.
 */
const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requireSuperadmin = false,
  companyAdminOnly = false,
  requireClinic = false,
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  const role = user?.role;
  const isSuperadmin = role === 'superadmin';
  const isCompanyAdmin = role === 'companyadmin';
  const isStaff = !isSuperadmin && !isCompanyAdmin;

  // Only superadmins bypass maintenance. Company staff are locked out of the
  // portal the same way recipients are, otherwise they could keep opening forms
  // and writing data while the system is meant to be quiet.
  const [maintenance, setMaintenance] = useState({ checked: false, active: false });
  const shouldCheckMaintenance = requireClinic || (requireAdmin && isCompanyAdmin);

  useEffect(() => {
    if (!shouldCheckMaintenance || !isAuthenticated) {
      setMaintenance({ checked: true, active: false });
      return undefined;
    }

    let cancelled = false;

    const check = async () => {
      try {
        const status = await formService.getMaintenanceStatus();
        if (cancelled) return;
        // Single state update: setting "checked" and "active" separately would
        // render the child page for one frame, long enough for it to fire its
        // own requests and show a 503 error toast.
        setMaintenance({
          checked: true,
          active: Boolean(status?.isActive) && !status?.isAdmin,
        });
      } catch {
        // Fail OPEN - a network blip should not lock staff out.
        if (!cancelled) setMaintenance({ checked: true, active: false });
      }
    };

    check();
    // Re-check so staff already inside the portal are moved out when a
    // window starts, and let back in once it ends.
    const id = setInterval(check, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [shouldCheckMaintenance, isAuthenticated]);


  if (loading || (shouldCheckMaintenance && isAuthenticated && !maintenance.checked)) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Platform-wide pages are off limits to everyone but the platform owner.
  if (requireSuperadmin && !isSuperadmin) {
    return <Navigate to={isCompanyAdmin ? '/admin/templates' : '/portal/templates'} replace />;
  }

  if (companyAdminOnly && isSuperadmin) {
    return <Navigate to="/admin/companies" replace />;
  }

  // Admin pages: the platform owner, or a company's own admin.
  if (requireAdmin && !isSuperadmin && !isCompanyAdmin) {
    return <Navigate to="/portal/templates" replace />;
  }

  // Company-scoped portal (staff). Admins are sent to the equivalent admin page.
  if (requireClinic && !isStaff) {
    const adminPath = location.pathname.replace(/^\/portal/, '/admin');
    return <Navigate to={adminPath || '/admin/templates'} replace />;
  }


  // Company staff are held at the maintenance page for the whole window.
  if (maintenance.active) {
    return <Navigate to="/maintenance" replace />;
  }

  return children;
};

export default ProtectedRoute;
